/**
 * Visit "Cleaner Absent" Drawer (Phase 7.B)
 *
 * ESM replacement for the inline ``<script>`` that used to live in
 * ``templates/office/visits/partials/_cleaner_absent_drawer.html``.
 *
 * Bespoke widgets handled here:
 *
 *   1. All-day toggle: a sr-only checkbox that hides/shows the time
 *      range fields. Initial state is "checked → hide times" because
 *      the absence default is a full-day record.
 *
 *   2. Drawer-scoped Datepicker boot: Flowbite's auto-init only walks
 *      the document once on page load. The drawer content is injected
 *      later via fetch + executeScripts, so we instantiate Datepicker
 *      ourselves on every ``[datepicker]`` input inside the form.
 *
 *   3. Per-field error UX (Phase 7.B-fix v3, 2026-05-10):
 *      - On submit, the server may return
 *        ``400 {success: false, error, field_errors: {field_name: msg}}``.
 *        We distribute each ``field_errors`` entry to the matching
 *        ``<p id="drawer_<field_name>_error">`` block under the input,
 *        and add ``border-red-500`` to the input itself.
 *      - The fallback ``#drawerCleanerAbsentFormError`` block in the
 *        footer only fires for non-field errors (e.g. cross-field
 *        replacement-cleaner cap).
 *      - Clearing: each per-field error block hides itself on
 *        ``input``/``change``/``changeDate``/``blur`` of its own
 *        input, and the footer block hides on any form-level event.
 *      - **No** ``input.focus()`` is called: focusing a Datepicker
 *        input pops the dropdown and hides the very error we just
 *        rendered.
 *      - **No** error toast: the form has dedicated inline containers,
 *        so the toast was redundant noise.
 *
 *   4. Validation pattern: backend strict + frontend clearing only.
 *      The server (``mark_cleaner_absent`` in
 *      ``apps/office/views/occurrences.py``) is the single source of
 *      truth for absence rules (required dates/times, dd/mm/yyyy
 *      format, end >= start, …). The client only sets ``required``
 *      natively for a11y / semantics — the form is ``novalidate`` so
 *      HTML5 native validation does not fire.
 *
 * Wraps with ``window.wireDrawerActionForm`` like the other Phase 7
 * modules — same submit semantics (lock button, POST FormData, refresh
 * schedule views, success toast, close drawer).
 *
 * Markup contract:
 *
 *   <div data-bespoke-init="visitCleanerAbsent"
 *        data-cleaner-absent-title="Cleaner Absent"
 *        data-cleaner-absent-success="Cleaner marked as absent"
 *        data-cleaner-absent-error="Error marking cleaner absent">
 *     <form id="drawerCleanerAbsentForm" novalidate …>
 *       <select id="drawer_absent_cleaner" required …>…</select>
 *       <p id="drawer_absent_cleaner_error" class="hidden …"></p>
 *       <select id="drawer_absence_reason" required …>…</select>
 *       <p id="drawer_absence_reason_error" class="hidden …"></p>
 *       <input type="checkbox" id="drawer_is_all_day" …>
 *       <input id="drawer_absence_start_date" datepicker required …>
 *       <p id="drawer_absence_start_date_error" class="hidden …"></p>
 *       <input id="drawer_absence_end_date" datepicker required …>
 *       <p id="drawer_absence_end_date_error" class="hidden …"></p>
 *       <div id="drawer_time_fields" class="hidden">
 *         <input id="drawer_absence_start_time" required …>
 *         <p id="drawer_absence_start_time_error" class="hidden …"></p>
 *         <input id="drawer_absence_end_time" required …>
 *         <p id="drawer_absence_end_time_error" class="hidden …"></p>
 *       </div>
 *     </form>
 *     <p id="drawerCleanerAbsentFormError" class="hidden …"></p>
 *     <button id="drawerCleanerAbsentSubmit" …>…</button>
 *   </div>
 */

// Map of server field-name → DOM ids for the matching input + inline
// error block. Keep in sync with the partial and the field_errors
// keys returned by mark_cleaner_absent.
const FIELD_ERROR_MAP = {
    absent_cleaner: { inputId: 'drawer_absent_cleaner', errorId: 'drawer_absent_cleaner_error' },
    absence_reason: { inputId: 'drawer_absence_reason', errorId: 'drawer_absence_reason_error' },
    absence_start_date: { inputId: 'drawer_absence_start_date', errorId: 'drawer_absence_start_date_error' },
    absence_end_date: { inputId: 'drawer_absence_end_date', errorId: 'drawer_absence_end_date_error' },
    absence_start_time: { inputId: 'drawer_absence_start_time', errorId: 'drawer_absence_start_time_error' },
    absence_end_time: { inputId: 'drawer_absence_end_time', errorId: 'drawer_absence_end_time_error' },
};

const FOOTER_ERROR_ID = 'drawerCleanerAbsentFormError';

function bootDrawerDatepickers(rootEl) {
    const form = rootEl.querySelector('#drawerCleanerAbsentForm');
    if (!form) return;
    if (typeof window.initFlowbite === 'function') {
        setTimeout(window.initFlowbite, 100);
    }
    setTimeout(() => {
        if (typeof window.Datepicker === 'undefined') return;
        form.querySelectorAll('[datepicker]').forEach((input) => {
            if (input._datepicker || input.datepicker) return;
            try {
                new window.Datepicker(input, {
                    format: 'dd/mm/yyyy',
                    autohide: true,
                    todayBtn: true,
                    clearBtn: false,
                });
            } catch (err) {
                console.warn('Datepicker init error:', err);
            }
        });
    }, 150);
}

function clearAllErrors(rootEl) {
    Object.values(FIELD_ERROR_MAP).forEach(({ inputId, errorId }) => {
        const errorEl = rootEl.querySelector('#' + errorId);
        const inputEl = rootEl.querySelector('#' + inputId);
        if (errorEl) {
            errorEl.classList.add('hidden');
            errorEl.textContent = '';
        }
        if (inputEl) {
            inputEl.classList.remove('border-red-500');
        }
    });
    const footerEl = rootEl.querySelector('#' + FOOTER_ERROR_ID);
    if (footerEl) {
        footerEl.classList.add('hidden');
        footerEl.textContent = '';
    }
}

function setupPerFieldClearing(rootEl) {
    Object.values(FIELD_ERROR_MAP).forEach(({ inputId, errorId }) => {
        const inputEl = rootEl.querySelector('#' + inputId);
        const errorEl = rootEl.querySelector('#' + errorId);
        if (!inputEl || !errorEl) return;
        const clear = () => {
            errorEl.classList.add('hidden');
            errorEl.textContent = '';
            inputEl.classList.remove('border-red-500');
        };
        ['input', 'change', 'changeDate', 'blur'].forEach((evt) => {
            inputEl.addEventListener(evt, clear);
        });
    });
}

function applyFieldErrors(rootEl, fieldErrors) {
    let mapped = 0;
    Object.entries(fieldErrors).forEach(([fieldName, message]) => {
        const map = FIELD_ERROR_MAP[fieldName];
        if (!map) return;
        const errorEl = rootEl.querySelector('#' + map.errorId);
        const inputEl = rootEl.querySelector('#' + map.inputId);
        if (errorEl) {
            errorEl.textContent = message;
            errorEl.classList.remove('hidden');
        }
        if (inputEl) {
            inputEl.classList.add('border-red-500');
        }
        mapped += 1;
    });
    return mapped;
}

function initVisitCleanerAbsentDrawer(rootEl) {
    if (!rootEl) return;
    const ds = rootEl.dataset;

    const titleEl = document.getElementById('drawerLabel');
    if (titleEl && ds.cleanerAbsentTitle) {
        titleEl.textContent = ds.cleanerAbsentTitle;
    }

    const allDayCheckbox = rootEl.querySelector('#drawer_is_all_day');
    const timeFields = rootEl.querySelector('#drawer_time_fields');
    function toggleTimeFields() {
        if (!allDayCheckbox || !timeFields) return;
        timeFields.classList.toggle('hidden', allDayCheckbox.checked);
    }
    toggleTimeFields();
    if (allDayCheckbox) {
        allDayCheckbox.addEventListener('change', toggleTimeFields);
    }

    bootDrawerDatepickers(rootEl);
    setupPerFieldClearing(rootEl);

    if (typeof window.wireDrawerActionForm !== 'function') return;

    window.wireDrawerActionForm({
        formId: 'drawerCleanerAbsentForm',
        submitBtnId: 'drawerCleanerAbsentSubmit',
        validators: [],
        expectJson: true,
        successMessage: ds.cleanerAbsentSuccess || '',
        errorMessage: ds.cleanerAbsentError || '',
        errorContainerId: FOOTER_ERROR_ID,
        // Per-field error distribution (no toast, inline only).
        // ``error.payload`` is set by drawer-action-form.js when the
        // server returns ``400 {success: false, error, field_errors}``.
        onError: (error, { showFormError }) => {
            clearAllErrors(rootEl);
            const payload = error.payload || {};
            const fieldErrors = payload.field_errors || {};
            const mapped = applyFieldErrors(rootEl, fieldErrors);
            if (mapped === 0) {
                showFormError(error.message || ds.cleanerAbsentError || '');
            }
        },
    });
}

if (typeof window !== 'undefined') {
    window.visitDrawerInits = window.visitDrawerInits || {};
    window.visitDrawerInits.visitCleanerAbsent = initVisitCleanerAbsentDrawer;
}

export { initVisitCleanerAbsentDrawer };
